import { CustomAppApiAuthProvider } from './CustomAppApiAuthProvider';
import {
  IContainer,
  IContainersPaginatedResponse,
} from '../common/ContainerSchemas';
import { HttpMethod } from '../common/Enums';
import { BaseApiProvider } from './BaseApiProvider';
import { Constants } from '../common/Constants';
import { CreateContainerDto } from '../dialogs/types/NewContainerTypes';

export class ContainersApiProvider extends BaseApiProvider {
  public readonly apiUrl: string = Constants.VITE_APP_API_URL;
  public static readonly instance: ContainersApiProvider =
    new ContainersApiProvider();
  private static readonly CACHE_KEY = 'containers_cache';
  private static readonly CACHE_EXPIRY_KEY = 'containers_cache_expiry';
  private static readonly PAGINATED_CACHE_KEY = 'containers_paginated_cache';
  private static readonly PAGINATED_CACHE_EXPIRY_KEY =
    'containers_paginated_cache_expiry';
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Request deduplication
  private pendingRequest: Promise<IContainer[]> | null = null;
  private pendingPaginatedRequests: Map<
    string,
    Promise<IContainersPaginatedResponse>
  > = new Map();

  private constructor() {
    super(CustomAppApiAuthProvider.instance);
  }

  private getCachedContainers(): IContainer[] | null {
    try {
      const cachedData = sessionStorage.getItem(
        ContainersApiProvider.CACHE_KEY,
      );
      const cacheExpiry = sessionStorage.getItem(
        ContainersApiProvider.CACHE_EXPIRY_KEY,
      );

      if (cachedData && cacheExpiry) {
        const expiryTime = parseInt(cacheExpiry, 10);
        const currentTime = Date.now();

        if (currentTime < expiryTime) {
          return JSON.parse(cachedData) as IContainer[];
        } else {
          // Cache expired, clear it
          this.clearCache();
        }
      }
    } catch (error) {
      console.warn('ContainersApiProvider: Failed to read cache:', error);
      this.clearCache();
    }

    return null;
  }

  private setCachedContainers(containers: IContainer[]): void {
    try {
      const expiryTime = Date.now() + ContainersApiProvider.CACHE_DURATION;
      sessionStorage.setItem(
        ContainersApiProvider.CACHE_KEY,
        JSON.stringify(containers),
      );
      sessionStorage.setItem(
        ContainersApiProvider.CACHE_EXPIRY_KEY,
        expiryTime.toString(),
      );
    } catch (error) {
      console.warn('ContainersApiProvider: Failed to cache containers:', error);
    }
  }

  private clearCache(): void {
    try {
      sessionStorage.removeItem(ContainersApiProvider.CACHE_KEY);
      sessionStorage.removeItem(ContainersApiProvider.CACHE_EXPIRY_KEY);
      sessionStorage.removeItem(ContainersApiProvider.PAGINATED_CACHE_KEY);
      sessionStorage.removeItem(
        ContainersApiProvider.PAGINATED_CACHE_EXPIRY_KEY,
      );
    } catch (error) {
      console.warn('ContainersApiProvider: Failed to clear cache:', error);
    }
  }

  private getCachedPaginatedPage(
    page: number,
    pageSize: number,
  ): IContainersPaginatedResponse | null {
    try {
      const cachedData = sessionStorage.getItem(
        ContainersApiProvider.PAGINATED_CACHE_KEY,
      );
      const cacheExpiry = sessionStorage.getItem(
        ContainersApiProvider.PAGINATED_CACHE_EXPIRY_KEY,
      );

      if (cachedData && cacheExpiry) {
        const expiryTime = parseInt(cacheExpiry, 10);
        const currentTime = Date.now();

        if (currentTime < expiryTime) {
          const cache = JSON.parse(cachedData) as Record<
            string,
            IContainersPaginatedResponse
          >;
          const cacheKey = `${page}_${pageSize}`;
          return cache[cacheKey] || null;
        } else {
          // Cache expired, clear it
          sessionStorage.removeItem(ContainersApiProvider.PAGINATED_CACHE_KEY);
          sessionStorage.removeItem(
            ContainersApiProvider.PAGINATED_CACHE_EXPIRY_KEY,
          );
        }
      }
    } catch (error) {
      console.warn(
        'ContainersApiProvider: Failed to read paginated cache:',
        error,
      );
    }

    return null;
  }

  private setCachedPaginatedPage(
    page: number,
    pageSize: number,
    response: IContainersPaginatedResponse,
  ): void {
    try {
      let cache: Record<string, IContainersPaginatedResponse> = {};
      const cachedData = sessionStorage.getItem(
        ContainersApiProvider.PAGINATED_CACHE_KEY,
      );

      if (cachedData) {
        cache = JSON.parse(cachedData) as Record<
          string,
          IContainersPaginatedResponse
        >;
      }

      const cacheKey = `${page}_${pageSize}`;
      cache[cacheKey] = response;

      const expiryTime = Date.now() + ContainersApiProvider.CACHE_DURATION;
      sessionStorage.setItem(
        ContainersApiProvider.PAGINATED_CACHE_KEY,
        JSON.stringify(cache),
      );
      sessionStorage.setItem(
        ContainersApiProvider.PAGINATED_CACHE_EXPIRY_KEY,
        expiryTime.toString(),
      );
    } catch (error) {
      console.warn(
        'ContainersApiProvider: Failed to cache paginated page:',
        error,
      );
    }
  }

  /**
   * Invalidates the containers cache. Useful when containers are modified.
   */
  public invalidateCache(): void {
    this.clearCache();
  }

  public async list(): Promise<IContainer[]> {
    // Check cache first
    const cachedContainers = this.getCachedContainers();
    if (cachedContainers) {
      console.debug('ContainersApiProvider: Using cached containers');
      return cachedContainers;
    }

    // Check if there's already a pending request
    if (this.pendingRequest) {
      console.debug(
        'ContainersApiProvider: Using pending request (deduplication)',
      );
      return this.pendingRequest;
    }

    // If not in cache and no pending request, create a new request
    console.debug('ContainersApiProvider: Fetching containers from API');
    this.pendingRequest = (async () => {
      const response = await this.listPaginated(1, 100);
      return response.containers;
    })();

    try {
      const containers = await this.pendingRequest;
      // Cache the results (listPaginated already includes "All" for page 1)
      this.setCachedContainers(containers);
      return containers;
    } finally {
      // Clear pending request
      this.pendingRequest = null;
    }
  }

  private async fetchContainers(): Promise<IContainer[]> {
    const token = await this.authProvider.getToken();
    const request = await this.createRequestInit(token, HttpMethod.GET);
    return (await this._send(
      this.apiUrl,
      '/containers',
      request,
    )) as IContainer[];
  }

  /**
   * List containers with pagination support.
   * Handles both paginated and non-paginated API responses for zero-downtime migration.
   * Caches each page separately to prevent redundant API calls.
   *
   * Strategy: Check cache first, then try paginated format, fall back to non-paginated if needed.
   */
  public async listPaginated(
    page: number = 1,
    pageSize: number = parseInt(Constants.VITE_APP_CONTAINER_PAGE_SIZE),
  ): Promise<IContainersPaginatedResponse> {
    // Check cache first
    const cachedPage = this.getCachedPaginatedPage(page, pageSize);
    if (cachedPage) {
      console.debug(`ContainersApiProvider: Using cached page ${page}`);
      return cachedPage;
    }

    // Check if there's already a pending request for this page
    const requestKey = `${page}_${pageSize}`;
    if (this.pendingPaginatedRequests.has(requestKey)) {
      console.debug(
        `ContainersApiProvider: Using pending request for page ${page} (deduplication)`,
      );
      return this.pendingPaginatedRequests.get(requestKey)!;
    }

    // Create a new request
    console.debug(`ContainersApiProvider: Fetching page ${page} from API`);
    const requestPromise = this.fetchPaginatedPage(page, pageSize);
    this.pendingPaginatedRequests.set(requestKey, requestPromise);

    try {
      const response = await requestPromise;
      // Cache the result
      this.setCachedPaginatedPage(page, pageSize, response);
      return response;
    } finally {
      // Clear pending request
      this.pendingPaginatedRequests.delete(requestKey);
    }
  }

  private async fetchPaginatedPage(
    page: number,
    pageSize: number,
  ): Promise<IContainersPaginatedResponse> {
    const token = await this.authProvider.getToken();
    const request = await this.createRequestInit(token, HttpMethod.GET);

    const response = await this._send(
      this.apiUrl,
      `/containers?page=${page}&page_size=${pageSize}`,
      request,
    );

    let result: IContainersPaginatedResponse;

    // Check if response is paginated format
    if (
      response &&
      typeof response === 'object' &&
      'containers' in response &&
      'page' in response &&
      'page_size' in response &&
      'total_count' in response
    ) {
      result = response as IContainersPaginatedResponse;
    } else if (Array.isArray(response)) {
      // Response is non-paginated (legacy format), wrap it
      console.debug(
        'ContainersApiProvider: Received non-paginated response, converting to paginated format',
      );
      result = {
        containers: response as IContainer[],
        page: page,
        page_size: pageSize,
        total_count: response.length,
      };
    } else {
      // Unexpected response format
      console.error(
        'ContainersApiProvider: Unexpected response format',
        response,
      );
      throw new Error('Invalid response format from containers API');
    }

    // Add "All" container to the end of the first page
    if (page === 1) {
      const allContainer: IContainer = {
        id: 'All',
        name: 'All',
      };
      result.containers.push(allContainer);
      result.total_count += 1; // Increment total count to include "All"
    }

    return result;
  }

  public async get(containerName: string): Promise<IContainer> {
    return (await this.list()).filter((c) => c.name === containerName)[0];
  }

  public async create(body: CreateContainerDto): Promise<IContainer> {
    const token = await this.authProvider.getToken();
    const request = await this.createRequestInit(token, HttpMethod.POST, body);
    const result = (await this._send(
      this.apiUrl,
      `/containers`,
      request,
    )) as IContainer;
    this.invalidateCache();
    return result;
  }

  public async delete(containerName: string): Promise<void> {
    const token = await this.authProvider.getToken();
    const request = await this.createRequestInit(token, HttpMethod.DELETE);

    await this._send(this.apiUrl, `/containers/${containerName}`, request);

    // Invalidate cache after deletion
    this.invalidateCache();
  }
}
